/**
 * Global search modal — opens on Cmd/Ctrl+K from anywhere in the
 * dashboard. Debounced, three sections (Users, Hubs, Posts), keyboard
 * navigable. Backed by GET /search/global.
 *
 * Phase 3 of launch readiness — first-cut experience. Tabbed deep
 * results / saved searches / autocomplete suggestions are out of
 * scope for this slice and slated for follow-ups.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Loader2, Hash, Users, FileText } from 'lucide-react';
import { useLocaleNavigate } from '@/hooks/useLocaleNavigate';
import { safeImageSrc } from '@/lib/url';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;

interface UserRow { id: string; username: string; display_name: string; avatar_url: string | null; }
interface HubRow { id: string; title: string; description: string | null; icon: string | null; follower_count: number; }
interface ScoopRow { id: string; title: string | null; description: string | null; image_url: string | null; author_username: string; author_display_name: string; }

interface Results { users: UserRow[]; hubs: HubRow[]; scoops: ScoopRow[]; }

export default function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cmd/Ctrl+K handler (and Escape to close).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Auto-focus when opened.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      // Reset state when closed so the next open is fresh.
      setQ('');
      setResults(null);
    }
  }, [open]);

  // Debounced fetch on query change.
  useEffect(() => {
    if (q.trim().length < MIN_CHARS) {
      setResults(null);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(
          `${API_URL}/search/global?q=${encodeURIComponent(q.trim())}&limit=6`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const data = await res.json();
        if (res.ok) setResults(data);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [q]);

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/4 hover:bg-white/8 text-sm text-[#a0a0b0] border border-white/6 transition-colors"
        aria-label={t('search.open', 'Open search')}
      >
        <Search size={14} />
        <span>{t('search.placeholder', 'Search Soltik')}</span>
        <kbd className="ml-2 text-[10px] text-[#9a9aac] font-mono border border-white/8 rounded px-1">⌘K</kbd>
      </button>
    );
  }

  const hasAny = results && (results.users.length || results.hubs.length || results.scoops.length);

  return (
    <div className="fixed inset-0 z-80 flex items-start justify-center pt-[10vh] px-4 bg-black/60 backdrop-blur-xs" onClick={() => setOpen(false)}>
      <div
        className="w-full max-w-2xl bg-[#16161e] border border-white/8 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/8">
          <Search size={16} className="text-[#a0a0b0]" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('search.placeholder', 'Search Soltik')}
            className="flex-1 bg-transparent text-white placeholder:text-[#9a9aac] focus:outline-hidden text-sm"
          />
          {loading && <Loader2 size={14} className="animate-spin text-[#a0a0b0]" />}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[#a0a0b0] hover:text-white"
            aria-label={t('search.close', 'Close')}
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q.trim().length < MIN_CHARS && (
            <div className="px-4 py-12 text-center text-sm text-[#9a9aac]">
              {t('search.hint', 'Type at least 2 characters to search.')}
            </div>
          )}

          {q.trim().length >= MIN_CHARS && !loading && results && !hasAny && (
            <div className="px-4 py-12 text-center text-sm text-[#a0a0b0]">
              {t('search.noResults', 'No results for "{q}".', { q })}
            </div>
          )}

          {results && (results.users.length > 0) && (
            <Section title={t('search.sections.users', 'People')} icon={<Users size={12} />}>
              {results.users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => go(`/u/${u.username}`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/3 text-left"
                >
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-iris-500/20 flex items-center justify-center text-xs text-iris-300">
                      {(u.display_name || u.username || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{u.display_name || u.username}</div>
                    <div className="text-xs text-[#9a9aac] truncate">@{u.username}</div>
                  </div>
                </button>
              ))}
            </Section>
          )}

          {results && (results.hubs.length > 0) && (
            <Section title={t('search.sections.hubs', 'Hubs')} icon={<Hash size={12} />}>
              {results.hubs.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => go(`/dashboard/hub/${h.id}`)}
                  className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-white/3 text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-xs">
                    {h.icon || '#'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{h.title}</div>
                    {h.description && (
                      <div className="text-xs text-[#9a9aac] truncate">{h.description}</div>
                    )}
                  </div>
                  <div className="text-xs text-[#9a9aac] shrink-0">
                    {h.follower_count} {t('search.followers', 'followers')}
                  </div>
                </button>
              ))}
            </Section>
          )}

          {results && (results.scoops.length > 0) && (
            <Section title={t('search.sections.scoops', 'Posts')} icon={<FileText size={12} />}>
              {results.scoops.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => go(`/dashboard/scoop/${s.id}`)}
                  className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-white/3 text-left"
                >
                  {s.image_url ? (
                    <img src={safeImageSrc(s.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-white/4 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white line-clamp-1">
                      {s.title || s.description || '(no title)'}
                    </div>
                    <div className="text-xs text-[#9a9aac] line-clamp-1">
                      {s.author_display_name} · @{s.author_username}
                    </div>
                  </div>
                </button>
              ))}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border-b border-white/4 last:border-b-0">
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9a9aac]">
        {icon}
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}
